if (!customElements.get('pdp-add-to-cart')) {
  customElements.define(
    'pdp-add-to-cart',
    class PDPAddToCart extends HTMLElement {
      debug(...args) {
        if (!window.PDP_SP_DEBUG) return;
        console.log('[pdp-add-to-cart]', ...args);
      }

      connectedCallback() {
        this.setup();
        this.bindEvents();
        this.bindPurchaseEvents();
        this.updateButtonText();
      }

      disconnectedCallback() {
        this.purchaseEvents?.abort();
        delete this.dataset.purchaseEventsBound;
      }

      setup() {
        this.productInfo = this.closest('product-info');
        this.formId = this.dataset.formId;
        this.form =
          (this.formId ? document.getElementById(this.formId) : null) ||
          this.productInfo?.querySelector('form[is="product-form"]') ||
          null;

        this.submitButtonId = this.dataset.submitButtonId || '';
        this.nativeSubmitButton =
          (this.submitButtonId ? document.getElementById(this.submitButtonId) : null) ||
          (this.submitButtonId ? this.productInfo?.querySelector(`#${this.submitButtonId}`) : null) ||
          null;
        if (!this.nativeSubmitButton && this.form) {
          this.nativeSubmitButton = this.form.querySelector('button[name="add"][type="submit"], button[type="submit"]');
        }

        this.ctaButton = this.querySelector('.pdp-add-to-cart__button');
        this.ctaText = this.querySelector('.pdp-add-to-cart__button-text');
      }

      bindEvents() {
        if (this.dataset.bound === 'true') return;
        this.addEventListener('click', async (event) => {
          const trigger = event.target.closest('.pdp-add-to-cart__button');
          if (!trigger) return;
          event.preventDefault();
          if (trigger.disabled || this.productInfo?.dataset.pdpVariantDisabled === 'true') return;

          this.setup();
          this.syncPurchaseModeToForm();
          this.updateButtonText();

          if (this.nativeSubmitButton) {
            if (this.nativeSubmitButton.disabled) {
              return;
            }
            this.nativeSubmitButton.click();
            return;
          }

          if (this.form) {
            this.form.requestSubmit();
            return;
          }

          await this.fallbackAddToCart();
        });
        this.dataset.bound = 'true';
      }

      bindPurchaseEvents() {
        if (this.dataset.purchaseEventsBound === 'true') return;
        if (!this.productInfo) return;
        this.purchaseEvents = new AbortController();
        const { signal } = this.purchaseEvents;

        this.productInfo.addEventListener('pdp:purchase-mode-changed', () => {
          this.setup();
          this.updateButtonText();
        }, { signal });

        this.productInfo.addEventListener('change', (event) => {
          if (!event.target.closest('.pdp-selling-plans__input')) return;
          this.setup();
          this.updateButtonText();
        }, { signal });

        this.dataset.purchaseEventsBound = 'true';
      }

      getPurchaseMode() {
        const oneTimeButton = this.productInfo?.querySelector('.pdp-one-time-purchase__button');
        if (oneTimeButton?.classList.contains('is-selected')) {
          return 'one-time';
        }

        if (this.productInfo?.dataset?.pdpPurchaseMode === 'one-time') {
          return 'one-time';
        }

        const selectedPlanInput = this.productInfo?.querySelector('.pdp-selling-plans__input:checked');
        if (selectedPlanInput) {
          return 'subscription';
        }

        return 'one-time';
      }

      getSelectedPlanId() {
        const selectedPlanInput = this.productInfo?.querySelector('.pdp-selling-plans__input:checked');
        return selectedPlanInput ? String(selectedPlanInput.value) : '';
      }

      updateButtonText() {
        if (!this.ctaText) return;
        if (this.dataset.variantAvailable === 'false') {
          this.ctaButton.disabled = true;
          this.ctaText.textContent = this.dataset.soldOutLabel || window.FoxTheme?.strings?.soldOut || 'Ausverkauft';
          return;
        }

        const mode = this.getPurchaseMode();
        if (mode === 'subscription') {
          const selectedPlanInput = this.productInfo?.querySelector('.pdp-selling-plans__input:checked');
          const planName = selectedPlanInput?.dataset?.planDisplayName?.trim();
          const planPrice = selectedPlanInput?.dataset?.planPrice?.trim();

          if (planName && planPrice) {
            this.ctaText.textContent = `🚀 Jetzt ${planName} starten - ${planPrice} € /mo`;
            return;
          }
        }

        const oneTimeRoot = this.productInfo?.querySelector('.pdp-one-time-purchase');
        const oneTimeLabel = oneTimeRoot?.dataset?.oneTimeLabel?.trim() || 'Einmaliger Kauf';
        const oneTimePrice = oneTimeRoot?.dataset?.oneTimePrice?.trim();
        if (oneTimePrice) {
          this.ctaText.textContent = `🚀 ${oneTimeLabel} - ${oneTimePrice} €`;
          return;
        }

        this.ctaText.textContent = `🚀 ${oneTimeLabel}`;
      }

      ensurePlanInput() {
        if (!this.form) return null;
        let planInput = this.form.querySelector('input[name="selling_plan"]');
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          this.form.appendChild(planInput);
        }
        return planInput;
      }

      syncPurchaseModeToForm() {
        const planInput = this.ensurePlanInput();
        if (!planInput) return;

        const mode = this.getPurchaseMode();
        if (mode === 'one-time') {
          planInput.value = '';
          return;
        }

        const selectedPlanId = this.getSelectedPlanId();
        planInput.value = selectedPlanId || '';
      }

      getCurrentVariantId() {
        if (this.form) {
          const variantInput = this.form.querySelector('[name="id"]');
          if (variantInput && variantInput.value) return variantInput.value;
        }

        const variantInputInProductInfo = this.productInfo?.querySelector('input.product-variant-id[name="id"]');
        if (variantInputInProductInfo?.value) return variantInputInProductInfo.value;

        const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
        if (!selectedVariant) return this.dataset.defaultVariantId || '';

        try {
          const variant = JSON.parse(selectedVariant);
          return variant?.id ? String(variant.id) : '';
        } catch (error) {
          return this.dataset.defaultVariantId || '';
        }
      }

      async fallbackAddToCart() {
        if (!window.FoxTheme?.routes?.cart_add_url || !window.FoxTheme?.utils?.fetchConfig) {
          this.debug('fallback unavailable: FoxTheme routes/utils missing');
          return;
        }

        const variantId = this.getCurrentVariantId();
        if (!variantId) {
          this.debug('fallback unavailable: variantId missing');
          return;
        }

        const quantity = this.form?.querySelector('[name="quantity"]')?.value || '1';
        const mode = this.getPurchaseMode();
        const selectedPlanId = mode === 'subscription' ? this.getSelectedPlanId() : '';

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        if (selectedPlanId) {
          formData.append('selling_plan', selectedPlanId);
        }
        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        try {
          const parsedState = await (await fetch(`${FoxTheme.routes.cart_add_url}`, config)).json();
          if (parsedState.status) {
            this.debug('fallback add failed', parsedState);
            return;
          }

          const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })).json();
          cartJson.sections = parsedState.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

          const cartDrawerElement = document.querySelector('cart-drawer');
          if (cartDrawerElement) {
            cartDrawerElement.show(this);
          }
        } catch (error) {
          console.error('[pdp-add-to-cart] fallback exception', error);
        }
      }
    }
  );
}
